import { useEffect, useState, type CSSProperties } from 'react';
import { X, Eye, Sparkles, Megaphone } from 'lucide-react';
import { AlertsSection } from './AlertsSection';
import { useAuth } from '../../hooks/useAuth';
import { useSettings } from '../../api/settings';
import { getViewAs, setViewAs, clearViewAs, getAiOverride, setAiOverride, clearAiOverride, getGraphEnabled, setGraphEnabled, getInfrastructureEnabled, setInfrastructureEnabled, getScanOverride, setScanOverride, clearScanOverride, type ViewAsRole } from '../../lib/view-as';
import { LT_BASE } from '../../lib/base-path';

type RealTier = 'superadmin' | 'admin' | 'engineer' | 'operator';
type Section = 'viewas' | 'features' | 'alerts';

interface ViewOption {
  id: ViewAsRole | null;
  label: string;
  description: string;
  minTier: RealTier;
}

const VIEW_OPTIONS: ViewOption[] = [
  { id: null, label: 'Your view', description: 'Full access based on your account role', minTier: 'operator' },
  { id: 'admin', label: 'Admin', description: 'Pace Board + identity management', minTier: 'superadmin' },
  { id: 'engineer', label: 'Engineer', description: 'Work queue + identity management', minTier: 'admin' },
  { id: 'operator', label: 'Operator', description: 'Escalation queue only', minTier: 'engineer' },
];

const TIER_ORDER: RealTier[] = ['operator', 'engineer', 'admin', 'superadmin'];

function tierIndex(t: RealTier): number {
  return TIER_ORDER.indexOf(t);
}

function canAccessOption(realTier: RealTier, minTier: RealTier): boolean {
  return tierIndex(realTier) >= tierIndex(minTier);
}

function realTierLabel(t: RealTier): string {
  return t;
}

export function EasterEggPanel({ onClose }: { onClose: () => void }) {
  const { isSuperAdmin, hasRoleType, hasRole } = useAuth();
  const { data: settings } = useSettings();
  const currentViewAs = getViewAs();
  const serverAiEnabled = !!settings?.ai?.enabled;
  const aiOverride = getAiOverride();
  const effectiveAiEnabled = aiOverride !== null ? aiOverride : serverAiEnabled;
  const [aiToggle, setAiToggle] = useState(effectiveAiEnabled);

  const serverGraph = settings?.features?.graphWorkflows;
  // Toggle is only user-controllable when server hasn't pinned it.
  const graphUserControlled = serverGraph === undefined || serverGraph === null;
  const [graphToggle, setGraphToggle] = useState(
    serverGraph === true || (graphUserControlled && getGraphEnabled()),
  );

  // Infrastructure (Routers, Messages, DB Maintenance) is builder-only and
  // opt-in on this device.
  const canSeeInfrastructure = isSuperAdmin || hasRole('engineer');
  const [infraToggle, setInfraToggle] = useState(getInfrastructureEnabled());

  // Scan input is opt-in: the deployment default (features.scanCodes, false
  // when omitted) unless a local override is set for testing.
  const serverScanEnabled = !!settings?.features?.scanCodes;
  const scanOverride = getScanOverride();
  const [scanToggle, setScanToggle] = useState(
    scanOverride !== null ? scanOverride : serverScanEnabled,
  );

  const realTier: RealTier = isSuperAdmin
    ? 'superadmin'
    : hasRoleType('admin')
      ? 'admin'
      : hasRole('engineer')
        ? 'engineer'
        : 'operator';

  const appName = settings?.branding?.appName ?? 'LongTail';
  const version = settings?.environment?.longTailVersion;
  const hotmeshVersion = settings?.environment?.hotmeshVersion;
  const nodeEnv = settings?.environment?.nodeEnv;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const availableOptions = VIEW_OPTIONS.filter((o) => canAccessOption(realTier, o.minTier));
  const hasViewOptions = availableOptions.length > 1;

  // The panel's concerns, each behind an icon in the segmented selector.
  // "Role declination" (view-as) only appears when relevant.
  // The publish route accepts role managers (superadmin, admin type,
  // engineer) — the tab mirrors that exactly.
  const canPublishAlerts = realTier !== 'operator';
  const sections: { id: Section; label: string; icon: typeof Eye }[] = [
    ...(hasViewOptions ? [{ id: 'viewas' as const, label: 'View As', icon: Eye }] : []),
    { id: 'features' as const, label: 'Features', icon: Sparkles },
    ...(canPublishAlerts ? [{ id: 'alerts' as const, label: 'Alerts', icon: Megaphone }] : []),
  ];
  const [section, setSection] = useState<Section>(sections[0].id);

  const handleSelect = (id: ViewAsRole | null) => {
    if (id === null) clearViewAs();
    else setViewAs(id);
  };

  const toggleAi = () => {
    const next = !aiToggle;
    setAiToggle(next);
    if (next === serverAiEnabled) clearAiOverride();
    else setAiOverride(next);
    window.location.reload();
  };

  const toggleGraph = () => {
    const next = !graphToggle;
    setGraphToggle(next);
    setGraphEnabled(next);
    window.location.reload();
  };

  const toggleInfrastructure = () => {
    const next = !infraToggle;
    setInfraToggle(next);
    setInfrastructureEnabled(next);
    window.location.reload();
  };

  const toggleScan = () => {
    const next = !scanToggle;
    setScanToggle(next);
    if (next === serverScanEnabled) clearScanOverride();
    else setScanOverride(next);
    window.location.reload();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-surface-sunken/75 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="relative w-[440px] max-h-[85vh] overflow-y-auto bg-surface-raised px-12 py-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-quaternary hover:text-text-secondary transition-colors p-1"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        {/* Identity */}
        <div className="flex flex-col items-center gap-3 pb-8">
          <span
            className="logo-mark block w-20 h-20 opacity-50"
            style={{ '--logo-url': `url(${LT_BASE}/logo512.png)` } as CSSProperties}
          />
          <div className="text-center">
            <p className="text-2xl font-light text-text-primary tracking-[0.12em]">{appName}</p>
            {version && (
              <p className="mt-1 text-2xs text-text-tertiary">
                v{version}
                {hotmeshVersion && <> · HotMesh v{hotmeshVersion}</>}
                {nodeEnv && nodeEnv !== 'production' && (
                  <span className="ml-1 text-status-warning">{nodeEnv}</span>
                )}
              </p>
            )}
          </div>
        </div>

        <hr className="border-surface-border/50 mb-6" />

        {/* Segmented icon selector — one concern at a time */}
        {sections.length > 1 && (
          <div className="flex items-center justify-center gap-2 mb-8" role="tablist">
            {sections.map((s) => {
              const active = section === s.id;
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setSection(s.id)}
                  title={s.label}
                  className={`flex flex-col items-center gap-1.5 px-5 py-2.5 rounded-lg transition-colors ${
                    active ? 'bg-accent/10 text-accent' : 'text-text-tertiary hover:text-text-secondary hover:bg-surface-hover'
                  }`}
                >
                  <s.icon className="w-5 h-5" strokeWidth={1.5} {...(active ? { fill: 'currentColor', fillOpacity: 0.15 } : {})} />
                  <span className="text-2xs font-medium">{s.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {section === 'viewas' && hasViewOptions && (
          <div className="space-y-1">
            <p className="text-2xs font-medium uppercase tracking-widest text-text-tertiary mb-4">View As</p>
            {availableOptions.map((opt) => {
              const isSelected = currentViewAs === opt.id;
              const label = opt.id === null ? `Your view · ${realTierLabel(realTier)}` : opt.label;
              return (
                <button
                  key={String(opt.id)}
                  onClick={() => handleSelect(opt.id)}
                  className="w-full text-left flex items-start gap-3 py-2.5 px-1 group"
                >
                  <span
                    className={`mt-0.5 w-4 h-4 rounded-full border shrink-0 flex items-center justify-center transition-colors ${
                      isSelected ? 'border-accent bg-accent' : 'border-surface-border group-hover:border-accent/60'
                    }`}
                  >
                    {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-text-inverse" />}
                  </span>
                  <span>
                    <span className={`block text-sm font-medium transition-colors ${
                      isSelected ? 'text-accent' : 'text-text-primary group-hover:text-accent'
                    }`}>
                      {label}
                    </span>
                    <span className="block text-xs text-text-secondary mt-0.5">{opt.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {section === 'features' && (
          <div>
            <p className="text-2xs font-medium uppercase tracking-widest text-text-tertiary mb-4">Features</p>
            <button
              onClick={toggleAi}
              className="w-full text-left flex items-center justify-between py-2.5 px-1 group"
            >
              <span>
                <span className="block text-sm font-medium text-text-primary group-hover:text-accent transition-colors">
                  AI features
                </span>
                <span className="block text-xs text-text-secondary mt-0.5">
                  {aiToggle ? 'Enabled — AI assist, agent labels, bulk AI actions' : 'Hidden — automation labels, no AI surfaces'}
                </span>
              </span>
              <span className={`shrink-0 ml-4 w-9 h-5 rounded-full transition-colors flex items-center px-0.5 ${aiToggle ? 'bg-accent' : 'bg-surface-border'}`}>
                <span className={`w-4 h-4 rounded-full bg-text-inverse shadow-sm transition-transform ${aiToggle ? 'translate-x-4' : 'translate-x-0'}`} />
              </span>
            </button>
            {graphUserControlled && (
              <button
                onClick={toggleGraph}
                className="w-full text-left flex items-center justify-between py-2.5 px-1 group"
              >
                <span>
                  <span className="block text-sm font-medium text-text-primary group-hover:text-accent transition-colors">
                    Graph workflows
                  </span>
                  <span className="block text-xs text-text-secondary mt-0.5">
                    {graphToggle ? 'Visible — Graph section shown in Orchestrate sidebar' : 'Hidden — use Procedural workflows instead'}
                  </span>
                </span>
                <span className={`shrink-0 ml-4 w-9 h-5 rounded-full transition-colors flex items-center px-0.5 ${graphToggle ? 'bg-accent' : 'bg-surface-border'}`}>
                  <span className={`w-4 h-4 rounded-full bg-text-inverse shadow-sm transition-transform ${graphToggle ? 'translate-x-4' : 'translate-x-0'}`} />
                </span>
              </button>
            )}
            {canSeeInfrastructure && (
              <button
                onClick={toggleInfrastructure}
                className="w-full text-left flex items-center justify-between py-2.5 px-1 group"
                data-testid="feature-infrastructure"
              >
                <span>
                  <span className="block text-sm font-medium text-text-primary group-hover:text-accent transition-colors">
                    Infrastructure
                  </span>
                  <span className="block text-xs text-text-secondary mt-0.5">
                    {infraToggle ? 'Visible — Routers, Messages, and DB Maintenance in the sidebar' : 'Hidden — routers, message streams, and database maintenance'}
                  </span>
                </span>
                <span className={`shrink-0 ml-4 w-9 h-5 rounded-full transition-colors flex items-center px-0.5 ${infraToggle ? 'bg-accent' : 'bg-surface-border'}`}>
                  <span className={`w-4 h-4 rounded-full bg-text-inverse shadow-sm transition-transform ${infraToggle ? 'translate-x-4' : 'translate-x-0'}`} />
                </span>
              </button>
            )}
            <button
              onClick={toggleScan}
              className="w-full text-left flex items-center justify-between py-2.5 px-1 group"
            >
              <span>
                <span className="block text-sm font-medium text-text-primary group-hover:text-accent transition-colors">
                  Scan input
                </span>
                <span className="block text-xs text-text-secondary mt-0.5">
                  {scanToggle ? 'Enabled — header scan affordance, panel, and keyboard-wedge capture' : 'Hidden — the deployment opts in with features.scanCodes'}
                </span>
              </span>
              <span className={`shrink-0 ml-4 w-9 h-5 rounded-full transition-colors flex items-center px-0.5 ${scanToggle ? 'bg-accent' : 'bg-surface-border'}`}>
                <span className={`w-4 h-4 rounded-full bg-text-inverse shadow-sm transition-transform ${scanToggle ? 'translate-x-4' : 'translate-x-0'}`} />
              </span>
            </button>
          </div>
        )}

        {section === 'alerts' && canPublishAlerts && <AlertsSection />}

        <hr className="border-surface-border/50 mt-8" />
        <p className="mt-4 text-2xs text-text-quaternary text-center">Esc or click outside to close</p>
      </div>
    </div>
  );
}
